import React, { useState, useRef } from 'react';
import { 
  Button, 
  Box, 
  Typography, 
  Alert, 
  CircularProgress,
  Link 
} from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import Papa from 'papaparse';
import { 
  collection, 
  writeBatch, 
  doc, 
  query, 
  where, 
  getDocs, 
  deleteDoc 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { validateAndProcessCsv } from '../../utils/csvProcessor';

const CsvImporter = ({ onComplete, onImportSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const findExistingVenues = async (venues) => {
    const placeIds = venues.map(venue => venue.place_id).filter(Boolean);
    if (placeIds.length === 0) return new Map();

    const existingVenues = new Map();
    const chunkSize = 30; // Firestore's IN query limit

    // Split placeIds into chunks of 30
    for (let i = 0; i < placeIds.length; i += chunkSize) {
      const chunk = placeIds.slice(i, i + chunkSize);
      
      const q = query(
        collection(db, 'venues'), 
        where('place_id', 'in', chunk)
      );
      
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach(doc => {
        existingVenues.set(doc.data().place_id, doc.id);
      });
    }

    return existingVenues;
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    Papa.parse(file, {
      header: true,
      complete: async (results) => {
        try {
          const venues = validateAndProcessCsv(results.data);
          
          // Find existing venues by place_id
          const existingVenues = await findExistingVenues(venues);
          const batch = writeBatch(db);

          // Process each venue
          venues.forEach(venue => {
            if (venue.place_id && existingVenues.has(venue.place_id)) {
              // Update existing venue
              const docId = existingVenues.get(venue.place_id);
              batch.set(doc(db, 'venues', docId), {
                ...venue,
                updated_at: new Date()
              });
            } else {
              // Add new venue
              const venueRef = doc(collection(db, 'venues'));
              batch.set(venueRef, {
                ...venue,
                created_at: new Date()
              });
            }
          });

          await batch.commit();
          
          const updatedCount = existingVenues.size;
          const newCount = venues.length - updatedCount;
          
          setSuccess(true);
          onComplete?.(`Successfully processed ${venues.length} venues (${newCount} new, ${updatedCount} updated)`);
          onImportSuccess?.();
        } catch (error) {
          console.error('Import error:', error);
          setError(error.message);
        } finally {
          setLoading(false);
          // Reset file input
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      },
      error: (error) => {
        setError(`Failed to parse CSV: ${error.message}`);
        setLoading(false);
        // Reset file input on error too
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    });
  };

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        id="csv-upload"
        onChange={handleFileUpload}
        disabled={loading}
      />
      


      <label htmlFor="csv-upload">
        <Button
          variant="contained"
          component="span"
          startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
          disabled={loading}
        >
          Upload CSV
        </Button>
        <Box sx={{ mb: 2 }}>
        <Link href="/sample-venues.csv" download>
          Download sample CSV template
        </Link>
      </Box>
      </label>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          CSV imported successfully!
        </Alert>
      )}
    </Box>
  );
};

export default CsvImporter;